import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { Menu, X } from "lucide-react";

// BEST PRACTICES:
// - No backdrop-blur (causes iOS Safari rendering issues on iPhone 15/16)
// - No LanguageContext (Polish-only landing page - avoid 1,328-line translation file)
// - Solid white background instead of transparency
// - Simple state, no complex animations
// - Native HTML elements where possible

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, loading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isHomePage = location.pathname === "/";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleNavClick = (item: { id: string; href?: string }) => {
    setIsMobileMenuOpen(false);
    if (item.href) {
      navigate(item.href);
    } else if (isHomePage) {
      const element = document.getElementById(item.id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/", { state: { scrollTo: item.id } });
    }
  };

  const navItems = [
    { label: "Jak to działa", id: "features" },
    { label: "Co zyskujesz", id: "porownanie" },
    { label: "Cennik", id: "pricing" },
  ];

  return (
    <header className="w-full fixed left-0 right-0 z-50 p-2 md:p-4" style={{ top: 'var(--banner-height, 0px)' }}>
      <div className="max-w-4xl mx-auto">
        {/* Glassmorphic header */}
        <div
          className="flex items-center justify-between px-4 md:px-6 py-2.5 md:py-3 rounded-full shadow-lg"
          style={{
            background: "rgba(255, 255, 255, 0.65)",
            backdropFilter: "blur(20px) saturate(150%)",
            WebkitBackdropFilter: "blur(20px) saturate(150%)",
            border: "1px solid rgba(255, 255, 255, 0.5)",
          }}
        >
          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5 text-gray-700" />
            ) : (
              <Menu className="h-5 w-5 text-gray-700" />
            )}
          </button>

          {/* Logo */}
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigate("/");
            }}
            className="font-bold text-base md:text-lg tracking-tight text-primary hover:opacity-80 transition-opacity"
          >
            Viraluj.pl
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.id}
                variant="ghost"
                size="sm"
                className="rounded-full text-gray-600 hover:text-gray-900 hover:bg-orange-50 text-sm font-medium px-4"
                onClick={() => handleNavClick(item)}
              >
                {item.label}
              </Button>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2">
            {loading ? (
              <div className="h-8 w-24 bg-gray-100 rounded-full animate-pulse" />
            ) : user ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/panel")}
                  className="text-gray-600 hover:text-gray-900 rounded-full"
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-gray-600 hover:text-gray-900 rounded-full"
                >
                  Wyloguj
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/auth?mode=login")}
                  className="text-gray-600 hover:text-gray-900 rounded-full font-medium"
                >
                  Zaloguj się
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/konsultacja")}
                  className="rounded-full font-medium border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/60 px-4"
                >
                  Bezpłatna konsultacja
                </Button>
                <Button
                  size="sm"
                  onClick={() => navigate("/auth?mode=register&trial=true")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-5 py-2 rounded-full shadow-md"
                >
                  Wypróbuj za darmo
                </Button>
              </>
            )}
          </div>

          {/* Mobile CTA */}
          <div className="md:hidden">
            {loading ? (
              <div className="h-8 w-20 bg-gray-100 rounded-full animate-pulse" />
            ) : user ? (
              <Button
                size="sm"
                onClick={() => navigate("/panel")}
                className="bg-primary text-white hover:bg-primary/90 font-bold px-4 py-1.5 rounded-full text-sm"
              >
                Panel
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => navigate("/auth?mode=register&trial=true")}
                className="bg-primary text-white hover:bg-primary/90 font-bold px-4 py-1.5 rounded-full text-sm"
              >
                Wypróbuj za darmo
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown - Simple, no animations that could cause iOS issues */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden mt-2 rounded-2xl shadow-lg overflow-hidden"
            style={{
              background: "rgba(255, 255, 255, 0.75)",
              backdropFilter: "blur(20px) saturate(150%)",
              WebkitBackdropFilter: "blur(20px) saturate(150%)",
              border: "1px solid rgba(255, 255, 255, 0.5)",
            }}
          >
            <nav className="p-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  className="w-full text-left px-4 py-3 rounded-xl text-gray-700 hover:bg-orange-50 hover:text-orange-600 font-medium transition-colors"
                  onClick={() => handleNavClick(item)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            {user ? (
              <div className="border-t border-gray-100 p-2">
                <button
                  className="w-full text-left px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                  onClick={() => {
                    navigate("/panel");
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Dashboard
                </button>
                <button
                  className="w-full text-left px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 font-medium"
                  onClick={() => {
                    handleSignOut();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Wyloguj
                </button>
              </div>
            ) : !loading && (
              <div className="border-t border-gray-100 p-2">
                <button
                  className="w-full text-left px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                  onClick={() => {
                    navigate("/auth?mode=login");
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Zaloguj się
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
